/*
 * This component displays a table of addresses with options to edit or delete each address.
 * It receives a list of addresses and optional callback functions for editing and deleting addresses.
 */

import React from 'react';
import { AddressData } from '../types/types';
import { MainButton, DeleteButton } from '../../../common';

// Props for the AddressesTable component
interface AddressesTableProps {
	addresses: AddressData[];
	onEdit: (id: number) => void;
	onDelete: (id: number) => void;
}

// AddressesTable component
const AddressesTable: React.FC<AddressesTableProps> = ({
	addresses,
	onEdit,
	onDelete,
}) => {
	if (addresses.length === 0) {
		return <p>Aucune adresse trouvée.</p>;
	}

	// Render the addresses table
	return (
		<div className="overflow-x-auto">
			{/* Table structure with headers and rows for each address */}
			<table className="min-w-full border border-gray-200 rounded-lg">
				{/* Table headers */}
				<thead className="bg-gray-50">
					<tr>
						<th className="px-6 py-3 text-left text-gray-400 font-medium border-b">
							ID
						</th>
						<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
							Label
						</th>
						<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
							Adresse
						</th>
						<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
							Actions
						</th>
					</tr>
				</thead>
				{/* Table body */}
				<tbody className="bg-white divide-y divide-gray-200">
					{addresses.map((address) => (
						<tr key={address.id}>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{address.id}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{address.label}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{address.line1}, {address.zipCode} {address.city},{' '}
								{address.country}
							</td>
							<td className="px-6 py-4 border-b space-x-2">
								<div className="flex space-x-2">
									{onEdit && (
										<MainButton
											label="Éditer"
											onClick={() => onEdit(address.id)}
											size="md"
										/>
									)}
									{onDelete && (
										<DeleteButton
											question={`Voulez-vous vraiment supprimer l'adresse ${address.line1}, ${address.city} ?`}
											onConfirm={() => onDelete(address.id)}
											size="md"
										/>
									)}
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default AddressesTable;
